import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Copy, Truck, Users, Building2, Globe, Mail, Phone, User, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { seriesTemplateAPI, tourLeaderAPI, hotelAPI } from '../../services/api';

interface ItineraryRow {
  sector: string;
  flightNumber: string;
  time: string;
  city: string;
  hotelName: string;
}

interface SeriesTemplate {
  id: string;
  seriesName: string;
  seriesCode: string;
  country: string;
  foreignTourOperator: string;
  defaultTransport?: string;
  itineraryTemplate: ItineraryRow[];
  defaultDuration?: number;
  defaultCities?: string[];
  defaultHotels?: string[];
  defaultProviders?: string[];
  createdAt: string;
  updatedAt: string;
}

interface HotelConfig {
  id: string;
  name: string;
  city: string;
  state?: string;
  address: string;
  phone?: string;
  email?: string;
  website: string;
  reservationEmail: string;
  salesPersonName: string;
  salesMobile: string;
  salesEmail: string;
  creditFacility?: boolean;
  creditTermsLimit?: string;
  bookingHoldDays?: number;
  paymentTimeline?: string;
  specialRemarks?: string;
}

type ConfigTab = 'series-templates' | 'hotels' | 'tour-leaders';

export function SeriesTemplatesPage() {
  const [activeTab, setActiveTab] = useState<ConfigTab>('series-templates');
  const [templates, setTemplates] = useState<SeriesTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [templateModalMode, setTemplateModalMode] = useState<'create' | 'edit' | 'duplicate'>('create');
  const [templateDraft, setTemplateDraft] = useState<SeriesTemplate | null>(null);

  // Tour Leaders state
  const [tourLeaders, setTourLeaders] = useState<{id: string; name: string}[]>([]);
  const [newLeaderName, setNewLeaderName] = useState('');

  // Hotels state
  const [hotels, setHotels] = useState<HotelConfig[]>([]);
  const [hotelsLoading, setHotelsLoading] = useState(false);
  const [showAddHotelModal, setShowAddHotelModal] = useState(false);
  const [showEditHotelModal, setShowEditHotelModal] = useState(false);
  const [editingHotel, setEditingHotel] = useState<HotelConfig | null>(null);

  useEffect(() => {
    loadTemplates();
    loadTourLeaders();
    loadHotels();
  }, []);

  // Hotels CRUD
  const loadHotels = async () => {
    try {
      setHotelsLoading(true);
      const response = await hotelAPI.getAll();
      if (response.success) {
        setHotels(response.data.hotels || []);
      }
    } catch (error) {
      console.error('Failed to load hotels:', error);
    } finally {
      setHotelsLoading(false);
    }
  };

  const handleDeleteHotel = async (id: string, name: string) => {
    if (!window.confirm(`Delete hotel "${name}"?`)) return;
    try {
      await hotelAPI.delete(id);
      setHotels(hotels.filter(h => h.id !== id));
    } catch (error: any) {
      alert(error.message || 'Failed to delete hotel');
    }
  };

  const handleOpenEditHotel = (hotel: HotelConfig) => {
    setEditingHotel(hotel);
    setShowEditHotelModal(true);
  };

  const handleHotelUpdated = (updatedHotel: HotelConfig) => {
    setHotels((prev) => prev.map((h) => (h.id === updatedHotel.id ? updatedHotel : h)));
    setShowEditHotelModal(false);
    setEditingHotel(null);
  };

  const loadTourLeaders = async () => {
    try {
      const response = await tourLeaderAPI.getAll();
      if (response.success) {
        setTourLeaders(response.data);
      }
    } catch (error) {
      console.error('Failed to load tour leaders:', error);
    }
  };

  const handleAddLeader = async () => {
    if (!newLeaderName.trim()) return;
    try {
      const response = await tourLeaderAPI.create(newLeaderName.trim());
      if (response.success) {
        setTourLeaders([...tourLeaders, response.data]);
        setNewLeaderName('');
      }
    } catch (error: any) {
      alert(error.message || 'Failed to add tour leader');
    }
  };

  const handleDeleteLeader = async (id: string, name: string) => {
    if (!window.confirm(`Delete tour leader "${name}"?`)) return;
    try {
      await tourLeaderAPI.delete(id);
      setTourLeaders(tourLeaders.filter(l => l.id !== id));
    } catch (error: any) {
      alert(error.message || 'Failed to delete tour leader');
    }
  };

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await seriesTemplateAPI.getAll();
      if (response.success) {
        setTemplates(response.data);
      }
    } catch (error) {
      console.error('Failed to load series templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}" series template?`)) {
      return;
    }

    try {
      await seriesTemplateAPI.delete(id);
      alert('Series template deleted successfully!');
      loadTemplates();
    } catch (error: any) {
      alert(`Failed to delete: ${error.message}`);
    }
  };

  const handleEdit = (template: SeriesTemplate) => {
    setTemplateDraft(template);
    setTemplateModalMode('edit');
    setShowAddModal(true);
  };

  const handleDuplicate = (template: SeriesTemplate) => {
    setTemplateDraft({
      ...template,
      id: '',
      seriesName: `${template.seriesName} Copy`,
      seriesCode: `${template.seriesCode}-COPY`,
      createdAt: '',
      updatedAt: ''
    });
    setTemplateModalMode('duplicate');
    setShowAddModal(true);
  };

  const handleModalClose = () => {
    setShowAddModal(false);
    setTemplateDraft(null);
    setTemplateModalMode('create');
    loadTemplates();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading templates...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Configuration Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Configuration</h1>
        <p className="text-gray-600 mt-1">Manage series templates, hotels, and tour leaders</p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('series-templates')}
          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'series-templates'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-2">
            <Copy className="w-4 h-4" />
            Series Templates
          </div>
        </button>
        <button
          onClick={() => setActiveTab('hotels')}
          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'hotels'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Hotels
          </div>
        </button>
        <button
          onClick={() => setActiveTab('tour-leaders')}
          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'tour-leaders'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Tour Leaders
          </div>
        </button>
      </div>

      {/* Series Templates Tab */}
      {activeTab === 'series-templates' && (
        <>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Series Templates</h2>
              <p className="text-gray-600 mt-1">
                Manage tour series templates for quick tour file creation
              </p>
            </div>
            <Button
              onClick={() => {
                setTemplateDraft(null);
                setTemplateModalMode('create');
                setShowAddModal(true);
              }}
              className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Series Template
            </Button>
          </div>

          {templates.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <Copy className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Series Templates Yet</h3>
              <p className="text-gray-600 mb-4">
                Create your first series template to streamline tour file creation
              </p>
              <Button
                onClick={() => {
                  setTemplateDraft(null);
                  setTemplateModalMode('create');
                  setShowAddModal(true);
                }}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Template
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{template.seriesName}</h3>
                        <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                          {template.seriesCode}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <p className="text-sm text-gray-500">Country</p>
                          <p className="font-medium text-gray-900">{template.country}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Foreign Tour Operator</p>
                          <p className="font-medium text-gray-900">{template.foreignTourOperator}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mt-3">
                        {template.defaultDuration && (
                          <div>
                            <p className="text-sm text-gray-500">Duration</p>
                            <p className="font-medium text-gray-900">{template.defaultDuration} days</p>
                          </div>
                        )}
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Transport Providers</p>
                          {template.defaultProviders && template.defaultProviders.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                              {template.defaultProviders.map((p, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium border border-blue-200">
                                  {p}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="font-medium text-gray-400 text-sm">None</p>
                          )}
                        </div>
                      </div>

                      <div className="mt-4">
                        <p className="text-sm text-gray-500 mb-2">Itinerary Preview</p>
                        <div className="bg-gray-50 rounded p-3 max-h-40 overflow-y-auto">
                          {template.itineraryTemplate && template.itineraryTemplate.length > 0 ? (
                            <div className="space-y-1 text-sm">
                              {template.itineraryTemplate.map((row, idx) => (
                                <div key={idx} className="flex gap-2 text-gray-700">
                                  <span className="font-medium">Day {idx + 1}:</span>
                                  <span>{row.sector}</span>
                                  {row.city && <span>→ {row.city}</span>}
                                  {row.hotelName && <span className="text-purple-600">({row.hotelName})</span>}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-400 text-sm">No itinerary defined</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 ml-4">
                      <Button
                        onClick={() => handleDuplicate(template)}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2 text-purple-700 hover:bg-purple-50"
                      >
                        <Copy className="w-4 h-4" />
                        Copy
                      </Button>
                      <Button
                        onClick={() => handleEdit(template)}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2"
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </Button>
                      <Button
                        onClick={() => handleDelete(template.id, template.seriesName)}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Hotels Tab */}
      {activeTab === 'hotels' && (
        <>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Hotels</h2>
              <p className="text-gray-600 mt-1">Manage hotels for tour assignments</p>
            </div>
            <Button
              onClick={() => setShowAddHotelModal(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Hotel
            </Button>
          </div>

          {hotelsLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading hotels...</div>
            </div>
          ) : hotels.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Hotels Yet</h3>
              <p className="text-gray-600 mb-4">Add your first hotel to the system</p>
              <Button
                onClick={() => setShowAddHotelModal(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Hotel
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {hotels.map((hotel) => (
                <div
                  key={hotel.id}
                  onClick={() => handleOpenEditHotel(hotel)}
                  className="group relative bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-lg hover:border-purple-300 transition-all cursor-pointer overflow-hidden"
                >
                  <div className="absolute -right-16 -top-16 w-40 h-40 rounded-full bg-gradient-to-br from-purple-50 to-indigo-50 opacity-60 pointer-events-none transition-transform duration-500 group-hover:scale-125" />
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-purple-700 transition-colors">{hotel.name}</h3>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-purple-700 bg-purple-50 border border-purple-100 rounded-md px-2 py-1">
                          <MapPin className="w-3 h-3" />
                          {hotel.city}{hotel.state ? `, ${hotel.state}` : ''}
                        </span>
                        {hotel.creditFacility && (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-2 py-1">
                            Credit Active
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                        <div className="flex items-center gap-1.5 text-gray-600">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <span>{hotel.city || 'N/A'}</span>
                        </div>
                        {hotel.website && (
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <Globe className="w-4 h-4 text-gray-400" />
                            <span className="truncate">{hotel.website}</span>
                          </div>
                        )}
                        {hotel.address && (
                          <div className="flex items-center gap-1.5 text-gray-600 col-span-2">
                            <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span>{hotel.address}</span>
                          </div>
                        )}
                        {hotel.phone && (
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span>{hotel.phone}</span>
                          </div>
                        )}
                        {hotel.email && (
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span className="truncate">{hotel.email}</span>
                          </div>
                        )}
                        {hotel.reservationEmail && (
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span>{hotel.reservationEmail}</span>
                          </div>
                        )}
                        {hotel.salesPersonName && (
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <User className="w-4 h-4 text-gray-400" />
                            <span>{hotel.salesPersonName}</span>
                          </div>
                        )}
                        {hotel.salesMobile && (
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span>{hotel.salesMobile}</span>
                          </div>
                        )}
                        {hotel.salesEmail && (
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span>{hotel.salesEmail}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditHotel(hotel);
                        }}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2 text-blue-700 hover:bg-blue-50"
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </Button>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteHotel(hotel.id, hotel.name);
                        }}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </Button>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-gray-100 text-sm text-purple-700 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    Click card to view and edit hotel information
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Tour Leaders Tab */}
      {activeTab === 'tour-leaders' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-bold text-gray-900">Tour Leaders</h2>
          </div>
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newLeaderName}
              onChange={(e) => setNewLeaderName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddLeader()}
              placeholder="Enter tour leader name..."
              className="flex-1 max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
            />
            <Button
              onClick={handleAddLeader}
              className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-1 text-sm"
              disabled={!newLeaderName.trim()}
            >
              <Plus className="w-4 h-4" />
              Add Tour Leader
            </Button>
          </div>
          {tourLeaders.length === 0 ? (
            <p className="text-gray-500 text-sm">No tour leaders added yet. Add your first tour leader above.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {tourLeaders.map((leader) => (
                <span
                  key={leader.id}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full text-sm font-medium border border-purple-200"
                >
                  {leader.name}
                  <button
                    onClick={() => handleDeleteLeader(leader.id, leader.name)}
                    className="text-purple-400 hover:text-red-600 transition-colors ml-1"
                    title="Remove tour leader"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {showAddModal && (
        <AddSeriesTemplateModal
          onClose={handleModalClose}
          template={templateDraft}
          mode={templateModalMode}
        />
      )}

      {showAddHotelModal && (
        <AddHotelModal
          onClose={() => {
            setShowAddHotelModal(false);
            loadHotels();
          }}
        />
      )}

      {showEditHotelModal && editingHotel && (
        <EditHotelModal
          hotel={editingHotel}
          onClose={() => {
            setShowEditHotelModal(false);
            setEditingHotel(null);
          }}
          onSaved={handleHotelUpdated}
        />
      )}
    </div>
  );
}

interface AddSeriesTemplateModalProps {
  onClose: () => void;
  template?: SeriesTemplate | null;
  mode?: 'create' | 'edit' | 'duplicate';
}

function AddSeriesTemplateModal({ onClose, template, mode = 'create' }: AddSeriesTemplateModalProps) {
  const isEditing = mode === 'edit' && !!template;
  
  const [formData, setFormData] = useState({
    seriesName: template?.seriesName || '',
    seriesCode: template?.seriesCode || '',
    country: template?.country || '',
    foreignTourOperator: template?.foreignTourOperator || ''
  });

  // Providers state (series-specific)
  const [providers, setProviders] = useState<string[]>(
    template?.defaultProviders || []
  );
  const [newProviderName, setNewProviderName] = useState('');

  const emptyRow: ItineraryRow = {
    sector: '',
    flightNumber: '',
    time: '',
    city: '',
    hotelName: ''
  };

  const [itineraryRows, setItineraryRows] = useState<ItineraryRow[]>(
    template?.itineraryTemplate && template.itineraryTemplate.length > 0
      ? template.itineraryTemplate
      : Array(5).fill(null).map(() => ({ ...emptyRow }))
  );

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItineraryChange = (index: number, field: keyof ItineraryRow, value: string) => {
    setItineraryRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addItineraryRow = () => {
    setItineraryRows(prev => [...prev, { ...emptyRow }]);
  };

  const removeItineraryRow = (index: number) => {
    if (itineraryRows.length <= 1) {
      alert('At least one itinerary row is required');
      return;
    }
    setItineraryRows(prev => prev.filter((_, i) => i !== index));
  };

  const copyItineraryRow = (index: number) => {
    const rowToCopy = itineraryRows[index];
    const newRow = { ...rowToCopy };
    setItineraryRows(prev => {
      const updated = [...prev];
      updated.splice(index + 1, 0, newRow);
      return updated;
    });
  };

  const handleAddProvider = () => {
    if (!newProviderName.trim()) return;
    if (providers.includes(newProviderName.trim())) {
      alert('Provider already added');
      return;
    }
    setProviders([...providers, newProviderName.trim()]);
    setNewProviderName('');
  };

  const handleRemoveProvider = (name: string) => {
    setProviders(providers.filter(p => p !== name));
  };

  const handleSave = async () => {
    // Validation
    if (!formData.seriesName || !formData.seriesCode || !formData.country || !formData.foreignTourOperator) {
      alert('Please fill in all required fields');
      return;
    }

    // Filter out empty itinerary rows
    const validItinerary = itineraryRows.filter(row =>
      row.sector || row.flightNumber || row.city || row.hotelName
    );

    const saveData = {
      ...formData,
      itineraryTemplate: validItinerary,
      defaultDuration: validItinerary.length,
      defaultCities: Array.from(new Set(validItinerary.map(r => r.city).filter(Boolean))),
      defaultHotels: Array.from(new Set(validItinerary.map(r => r.hotelName).filter(Boolean))),
      defaultProviders: providers
    };

    try {
      if (isEditing && template) {
        await seriesTemplateAPI.update(template.id, saveData);
        alert('✅ Series template updated successfully!');
      } else {
        await seriesTemplateAPI.create(saveData);
        alert('✅ Series template created successfully!');
      }
      onClose();
    } catch (error: any) {
      alert(`❌ Failed to save: ${error.message}`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">
            {mode === 'edit' ? 'Edit Series Template' : mode === 'duplicate' ? 'Duplicate Series Template' : 'Add New Series Template'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="p-6">
          {/* Basic Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">Series Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Series Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.seriesName}
                  onChange={(e) => handleInputChange('seriesName', e.target.value)}
                  placeholder="e.g., Golden Triangle"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Series Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.seriesCode}
                  onChange={(e) => handleInputChange('seriesCode', e.target.value)}
                  placeholder="e.g., GT"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  placeholder="e.g., Australia"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Foreign Tour Operator <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.foreignTourOperator}
                  onChange={(e) => handleInputChange('foreignTourOperator', e.target.value)}
                  placeholder="e.g., Inspiring Vacations"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
          </div>

          {/* Transport Providers (series-specific) */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Truck className="w-4 h-4 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Transport Providers</h3>
            </div>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newProviderName}
                onChange={(e) => setNewProviderName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddProvider()}
                placeholder="Enter provider name..."
                className="flex-1 max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <Button
                onClick={handleAddProvider}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1 text-sm"
                disabled={!newProviderName.trim()}
              >
                <Plus className="w-4 h-4" />
                Add Provider
              </Button>
            </div>
            {providers.length === 0 ? (
              <p className="text-gray-400 text-sm">No providers added. Add transport providers for this series.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {providers.map((name, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium border border-blue-200"
                  >
                    {name}
                    <button
                      onClick={() => handleRemoveProvider(name)}
                      className="text-blue-400 hover:text-red-600 transition-colors ml-1"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Itinerary Template */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Itinerary Template</h3>
              <Button
                onClick={addItineraryRow}
                size="sm"
                variant="outline"
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Row
              </Button>
            </div>

            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sector</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Flight/Train</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">City</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hotel Name</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {itineraryRows.map((row, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={row.sector}
                            onChange={(e) => handleItineraryChange(index, 'sector', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={row.flightNumber}
                            onChange={(e) => handleItineraryChange(index, 'flightNumber', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={row.time}
                            onChange={(e) => handleItineraryChange(index, 'time', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={row.city}
                            onChange={(e) => handleItineraryChange(index, 'city', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={row.hotelName}
                            onChange={(e) => handleItineraryChange(index, 'hotelName', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => copyItineraryRow(index)}
                              className="text-blue-600 hover:text-blue-800 p-1"
                              title="Copy row"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => removeItineraryRow(index)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Remove row"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              onClick={onClose}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {isEditing ? 'Update Template' : 'Create Template'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Add Hotel Modal
function AddHotelModal({ onClose }: { onClose: () => void }) {
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    website: '',
    address: '',
    reservationEmail: '',
    salesPersonName: '',
    salesMobile: '',
    salesEmail: '',
    creditFacility: false,
    creditTermsLimit: '',
    bookingHoldDays: 0,
    paymentTimeline: '',
    specialRemarks: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.city.trim()) {
      alert('Hotel Name and City/Location are required');
      return;
    }

    try {
      setSaving(true);
      await hotelAPI.create(formData);
      alert('Hotel added successfully!');
      onClose();
    } catch (error: any) {
      alert(`Failed to add hotel: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">Add New Hotel</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold">×</button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hotel Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., The Regent Grand"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City / Location <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="e.g., Delhi"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
            <input
              type="text"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              placeholder="e.g., https://www.hotel.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Full address"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reservation Email ID</label>
            <input
              type="email"
              value={formData.reservationEmail}
              onChange={(e) => setFormData({ ...formData, reservationEmail: e.target.value })}
              placeholder="reservations@hotel.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sales Person Name</label>
              <input
                type="text"
                value={formData.salesPersonName}
                onChange={(e) => setFormData({ ...formData, salesPersonName: e.target.value })}
                placeholder="Sales person name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sales Mobile Number</label>
              <input
                type="text"
                value={formData.salesMobile}
                onChange={(e) => setFormData({ ...formData, salesMobile: e.target.value })}
                placeholder="Mobile number"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sales Email ID</label>
            <input
              type="email"
              value={formData.salesEmail}
              onChange={(e) => setFormData({ ...formData, salesEmail: e.target.value })}
              placeholder="sales@hotel.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* New Fields Section */}
          <div className="border-t pt-4 mt-4">
            <h3 className="font-semibold text-gray-900 mb-3">Credit & Payment Management</h3>
            
            <div className="flex items-center gap-3 mb-4">
              <input
                type="checkbox"
                id="creditFacility"
                checked={formData.creditFacility}
                onChange={(e) => setFormData({ ...formData, creditFacility: e.target.checked })}
                className="w-4 h-4 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
              />
              <label htmlFor="creditFacility" className="text-sm font-medium text-gray-700">
                Credit Facility Available?
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Credit Terms / Limit</label>
              <input
                type="text"
                value={formData.creditTermsLimit}
                onChange={(e) => setFormData({ ...formData, creditTermsLimit: e.target.value })}
                placeholder="e.g., Net 15 / INR 500000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Booking Hold Without Payment (Days)</label>
              <input
                type="number"
                value={formData.bookingHoldDays}
                onChange={(e) => setFormData({ ...formData, bookingHoldDays: parseInt(e.target.value) || 0 })}
                placeholder="e.g., 7"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Timeline If No Credit</label>
              <input
                type="text"
                value={formData.paymentTimeline}
                onChange={(e) => setFormData({ ...formData, paymentTimeline: e.target.value })}
                placeholder="e.g., 100% advance or on request"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes / Special Remarks</label>
              <textarea
                value={formData.specialRemarks}
                onChange={(e) => setFormData({ ...formData, specialRemarks: e.target.value })}
                placeholder="Any special terms or requirements..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button onClick={onClose} variant="outline">Cancel</Button>
            <Button
              onClick={handleSave}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              disabled={saving}
            >
              {saving ? 'Adding...' : 'Add Hotel'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditHotelModal({
  hotel,
  onClose,
  onSaved
}: {
  hotel: HotelConfig;
  onClose: () => void;
  onSaved: (updatedHotel: HotelConfig) => void;
}) {
  const [formData, setFormData] = useState({
    name: hotel.name || '',
    city: hotel.city || '',
    state: hotel.state || '',
    website: hotel.website || '',
    address: hotel.address || '',
    phone: hotel.phone || '',
    email: hotel.email || '',
    reservationEmail: hotel.reservationEmail || '',
    salesPersonName: hotel.salesPersonName || '',
    salesMobile: hotel.salesMobile || '',
    salesEmail: hotel.salesEmail || '',
    creditFacility: hotel.creditFacility || false,
    creditTermsLimit: hotel.creditTermsLimit || '',
    bookingHoldDays: hotel.bookingHoldDays || 0,
    paymentTimeline: hotel.paymentTimeline || '',
    specialRemarks: hotel.specialRemarks || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.city.trim()) {
      alert('Hotel Name and City/Location are required');
      return;
    }

    try {
      setSaving(true);
      const response = await hotelAPI.update(hotel.id, formData);
      if (response.success) {
        onSaved(response.data?.hotel || { ...hotel, ...formData });
      }
    } catch (error: any) {
      alert(`Failed to update hotel: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">Edit Hotel</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold">×</button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hotel Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City / Location <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
              <input
                type="text"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reservation Email ID</label>
              <input
                type="email"
                value={formData.reservationEmail}
                onChange={(e) => setFormData({ ...formData, reservationEmail: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sales Person Name</label>
              <input
                type="text"
                value={formData.salesPersonName}
                onChange={(e) => setFormData({ ...formData, salesPersonName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sales Mobile Number</label>
              <input
                type="text"
                value={formData.salesMobile}
                onChange={(e) => setFormData({ ...formData, salesMobile: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sales Email ID</label>
              <input
                type="email"
                value={formData.salesEmail}
                onChange={(e) => setFormData({ ...formData, salesEmail: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <h3 className="font-semibold text-gray-900 mb-3">Credit & Payment Management</h3>

            <div className="flex items-center gap-3 mb-4">
              <input
                type="checkbox"
                id="editCreditFacility"
                checked={formData.creditFacility}
                onChange={(e) => setFormData({ ...formData, creditFacility: e.target.checked })}
                className="w-4 h-4 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
              />
              <label htmlFor="editCreditFacility" className="text-sm font-medium text-gray-700">
                Credit Facility Available?
              </label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Credit Terms / Limit</label>
                <input
                  type="text"
                  value={formData.creditTermsLimit}
                  onChange={(e) => setFormData({ ...formData, creditTermsLimit: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Booking Hold Without Payment (Days)</label>
                <input
                  type="number"
                  value={formData.bookingHoldDays}
                  onChange={(e) => setFormData({ ...formData, bookingHoldDays: parseInt(e.target.value, 10) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Timeline If No Credit</label>
              <input
                type="text"
                value={formData.paymentTimeline}
                onChange={(e) => setFormData({ ...formData, paymentTimeline: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes / Special Remarks</label>
              <textarea
                value={formData.specialRemarks}
                onChange={(e) => setFormData({ ...formData, specialRemarks: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button onClick={onClose} variant="outline">Cancel</Button>
            <Button
              onClick={handleSave}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
